import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { products } from "@/data/products";

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find((p) => p.id === id);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const galleryImages = product?.images?.length ? product.images : product ? [product.image] : [];
  const [selectedImage, setSelectedImage] = useState<string>(galleryImages[0] ?? "");

  useEffect(() => {
    setSelectedImage(galleryImages[0] ?? "");
  }, [galleryImages]);

  useEffect(() => {
    const setMeta = (selector: string, content: string) => {
      const meta = document.querySelector<HTMLMetaElement>(selector);
      if (meta) {
        meta.setAttribute("content", content);
      }
    };

    if (product) {
      document.title = `${product.name} | Catalogo`;
      setMeta('meta[name="description"]', product.description);
      setMeta('meta[property="og:title"]', product.name);
      setMeta('meta[property="og:description"]', product.description);
      return;
    }

    document.title = "Producto no encontrado | Catalogo";
    setMeta('meta[name="description"]', "Descubre nuestros productos en el catalogo.");
    setMeta('meta[property="og:title"]', "Producto no encontrado");
    setMeta('meta[property="og:description"]', "Descubre nuestros productos en el catalogo.");
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-display mb-2">Producto no encontrado</h1>
            <Link to="/" className="text-sm text-muted-foreground underline underline-offset-4">
              Volver al catálogo
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const whatsappPhone = "593987038437";
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const whatsappMessage = [
    "Hola, quiero comprar este producto:",
    `Producto: ${product.name}`,
    `Precio: $${product.price.toFixed(2)}`,
    `Talla: ${selectedSize ?? "No seleccionada"}`,
    `Color: ${selectedColor ?? "No seleccionado"}`,
    currentUrl ? `Link: ${currentUrl}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  const whatsappUrl = `https://api.whatsapp.com/send/?phone=${whatsappPhone}&text=${encodeURIComponent(whatsappMessage)}&type=phone_number&app_absent=0`;
  const isWhatsappDisabled = !selectedSize || !selectedColor;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 px-4 sm:px-6 py-8 bg-background">
        <div className=" md:container mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
          {/* Image gallery */}
          <div className="space-y-4">
            <div className="aspect-[1/1] bg-secondary overflow-hidden">
              <img
                src={selectedImage}
                alt={product.name}
                className="w-full h-full object-cover object-center"
              />
            </div>

            {galleryImages.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                {galleryImages.map((image, index) => {
                  const isActive = selectedImage === image;

                  return (
                    <button
                      key={`${product.id}-${index}`}
                      type="button"
                      onClick={() => setSelectedImage(image)}
                      className={`aspect-[1/1] overflow-hidden border transition-colors ${
                        isActive
                          ? "border-foreground"
                          : "border-border hover:border-foreground/60"
                      }`}
                      aria-label={`Ver imagen ${index + 1} de ${galleryImages.length}`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} vista ${index + 1}`}
                        className="w-full h-full object-cover object-center"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center py-4">
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">
              {product.category}
            </p>
            <h1 className="text-2xl sm:text-3xl font-display font-medium tracking-tight">
              {product.name}
            </h1>
            <p className="text-lg mt-2 mb-6">${product.price.toFixed(2)}</p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Sizes */}
            <div className="mb-8">
              <h3 className="text-sm tracking-widest uppercase mb-3">Tallas</h3>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`min-w-[44px] h-10 px-3 border border-gray-400 text-sm transition-colors ${
                      selectedSize === size
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-foreground border-bor hover:border-foreground"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="mb-8">
              <h3 className="text-sm tracking-widest uppercase mb-3">Colores disponibles</h3>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setSelectedColor(color)}
                    className={`px-3 py-1.5 border border-gray-400 text-sm transition-colors ${
                      selectedColor === color
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-foreground border-border hover:border-foreground"
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              disabled={isWhatsappDisabled}
              onClick={() => window.open(whatsappUrl, "_blank", "noopener,noreferrer")}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageCircle className="h-4 w-4" />
              Comprar por WhatsApp
            </button>

            {isWhatsappDisabled && (
              <p className="text-sm text-red-600">
                Selecciona la talla y el color para continuar.
              </p>
            )}

            {/* Details */}
            <div>
              <h3 className="text-sm tracking-widest uppercase mb-3 mt-8">Detalles</h3>
              <ul className="space-y-1.5">
                {product.details.map((d, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
          
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
